import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "../../../windowManager/browserFenster/fensterPortal";
import { ApiFetchError } from "../../../../utils/apiFetch";
import {
    rechtschreibAusfallMerken,
    rechtschreibPruefen,
    rechtschreibStatus,
    type RechtschreibTreffer,
} from "../../../../services/rechtschreibService";
import { bereichFuer, bildeAbbild, type TextAbbild } from "./textAbbild";
import { entferneMarkierungen, highlightsMoeglich, setzeMarkierungen } from "./markierungen";
import "./rechtschreibung.css";
import { alleDokumente, alleFenster, aktiveAuswahl, aktivesDokument, aktivesFenster } from "../../../windowManager/browserFenster/fensterRegister";

/**
 * Rechtschreib- und Grammatikprüfung im Mail-Editor (T-243).
 *
 * Arbeitet ausschließlich von außen am `contentEditable`: beobachtet Änderungen
 * (MutationObserver), prüft nach einer Tipp-Pause im Hintergrund und
 * unterstreicht per CSS Custom Highlight API. Der Editor-DOM wird nur beim
 * Übernehmen eines Vorschlags verändert — und dann über
 * `execCommand("insertText")`, damit Formatierung und Strg+Z erhalten bleiben.
 *
 * Ist der Dienst nicht verfügbar oder kann der Browser keine Highlights, bleibt
 * `aktiv` false; der Editor nutzt dann wie bisher die Prüfung des Browsers.
 * Fehler werden nie angezeigt — die Nutzer sollen nicht mit Technik belästigt
 * werden.
 */

/** Tipp-Pause, nach der geprüft wird. */
export const PRUEF_PAUSE_MS = 800;

interface Eintrag {
    treffer: RechtschreibTreffer;
    /** Live-`Range`: wandert bei Änderungen im DOM mit. */
    bereich: Range;
    /** Wortlaut beim Prüfen — Schutz gegen Übernahme an verschobener Stelle. */
    wort: string;
}

interface PopupZustand {
    eintrag: Eintrag;
    top: number;
    left: number;
    aktiv: number;
}

export interface RechtschreibungApi {
    /** Prüfung läuft (Dienst erreichbar und Browser kann Highlights). */
    aktiv: boolean;
    /** Zahl der offenen Hinweise. */
    anzahl: number;
    /** Springt zum ersten Hinweis und öffnet dessen Vorschläge. */
    zumErsten(): boolean;
    /** Das Vorschlags-Popup (portaliert) — vom Editor einfach mitrendern. */
    popup: React.ReactNode;
}

const POPUP_BREITE = 280;

/** DOM-Punkt unter dem Mauszeiger (Firefox: caretPositionFromPoint, sonst caretRangeFromPoint). */
function punktAn(x: number, y: number): { node: Node; offset: number } | null {
    const d = document as Document & {
        caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
        caretRangeFromPoint?: (x: number, y: number) => Range | null;
    };
    const p = d.caretPositionFromPoint?.(x, y);
    if (p) return { node: p.offsetNode, offset: p.offset };
    const r = d.caretRangeFromPoint?.(x, y);
    return r ? { node: r.startContainer, offset: r.startOffset } : null;
}

const ignorierSchluessel = (regel: string, wort: string) => `${regel}\u0000${wort}`;

export interface RechtschreibOptionen {
    /**
     * Schon beim Einschalten prüfen, wenn Text da ist (Standard, Mail-Entwurf).
     * Das Notizfeld im Vorgang prüft erst nach der ersten Änderung — sonst
     * stünde jede alte Notiz beim Öffnen voller Unterstreichungen, und ein
     * Vorgang mit vielen To-dos schickte auf einmal viele Anfragen.
     */
    sofortPruefen?: boolean;
}

export function useRechtschreibung(
    rootRef: React.RefObject<HTMLElement>,
    eingeschaltet: boolean,
    { sofortPruefen = true }: RechtschreibOptionen = {},
): RechtschreibungApi {
    const quelle = useId();
    const [aktiv, setAktiv] = useState(false);
    const [anzahl, setAnzahl] = useState(0);
    const [popup, setPopup] = useState<PopupZustand | null>(null);
    const popupRef = useRef<PopupZustand | null>(null);
    popupRef.current = popup;
    const popupElRef = useRef<HTMLDivElement>(null);

    const eintraegeRef = useRef<Eintrag[]>([]);
    const ignoriertRef = useRef(new Set<string>());
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const anfrageRef = useRef<AbortController | null>(null);
    /** Zuletzt erfolgreich geprüfter Text und seine Treffer. */
    const letzteRef = useRef<{ text: string; treffer: RechtschreibTreffer[] } | null>(null);

    // ── Verfügbarkeit ────────────────────────────────────────────────────
    useEffect(() => {
        if (!eingeschaltet || !highlightsMoeglich()) {
            setAktiv(false);
            return;
        }
        let lebt = true;
        void rechtschreibStatus().then((ok) => {
            if (lebt) setAktiv(ok);
        });
        return () => {
            lebt = false;
        };
    }, [eingeschaltet]);

    // ── Markierungen ─────────────────────────────────────────────────────
    const zeigen = useCallback(
        (eintraege: Eintrag[]) => {
            eintraegeRef.current = eintraege;
            setzeMarkierungen(quelle, {
                rechtschreibung: eintraege.filter((e) => e.treffer.art === "rechtschreibung").map((e) => e.bereich),
                grammatik: eintraege.filter((e) => e.treffer.art !== "rechtschreibung").map((e) => e.bereich),
            });
            setAnzahl(eintraege.length);
        },
        [quelle],
    );

    const uebernehmeTreffer = useCallback(
        (abbild: TextAbbild, treffer: RechtschreibTreffer[]) => {
            const eintraege: Eintrag[] = [];
            for (const t of treffer) {
                const wort = abbild.text.slice(t.offset, t.offset + t.laenge);
                if (ignoriertRef.current.has(ignorierSchluessel(t.regel, wort))) continue;
                const bereich = bereichFuer(abbild, t.offset, t.laenge);
                if (bereich) eintraege.push({ treffer: t, bereich, wort });
            }
            zeigen(eintraege);
        },
        [zeigen],
    );

    const pruefen = useCallback(() => {
        const root = rootRef.current;
        if (!root) return;
        const abbild = bildeAbbild(root);
        if (!abbild.text.trim()) {
            anfrageRef.current?.abort();
            letzteRef.current = null;
            zeigen([]);
            return;
        }
        // Gleicher Text (z. B. nur Formatierung geändert): Treffer neu verorten,
        // ohne erneut zu fragen.
        if (letzteRef.current?.text === abbild.text) {
            uebernehmeTreffer(abbild, letzteRef.current.treffer);
            return;
        }
        anfrageRef.current?.abort();
        const ctrl = new AbortController();
        anfrageRef.current = ctrl;
        rechtschreibPruefen(abbild.text, ctrl.signal)
            .then((treffer) => {
                if (ctrl.signal.aborted || !rootRef.current) return;
                // Inzwischen weitergetippt? Dann passen die Positionen nicht
                // mehr — verwerfen, die nächste Pause prüft erneut.
                const jetzt = bildeAbbild(rootRef.current);
                if (jetzt.text !== abbild.text) return;
                letzteRef.current = { text: abbild.text, treffer };
                uebernehmeTreffer(jetzt, treffer);
            })
            .catch((e: unknown) => {
                if (ctrl.signal.aborted) return;
                // Dienst weg: still zurück auf die Prüfung des Browsers.
                if (e instanceof ApiFetchError && e.status === 503) {
                    rechtschreibAusfallMerken();
                    setAktiv(false);
                }
                // Sonst (429, Netz): die nächste Pause versucht es erneut.
            });
    }, [rootRef, uebernehmeTreffer, zeigen]);

    // ── Beobachten und nach der Tipp-Pause prüfen ───────────────────────
    useEffect(() => {
        const root = rootRef.current;
        if (!aktiv || !root) return;
        const planen = (ms: number) => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(pruefen, ms);
        };
        const beobachter = new MutationObserver(() => {
            // Wer tippt, ist mit dem Popup fertig.
            if (popupRef.current) setPopup(null);
            planen(PRUEF_PAUSE_MS);
        });
        beobachter.observe(root, { childList: true, characterData: true, subtree: true });
        // Einmal beim Öffnen, falls schon Text da ist (Antwort, Entwurf).
        if (sofortPruefen) planen(0);
        return () => {
            beobachter.disconnect();
            if (timerRef.current) clearTimeout(timerRef.current);
            anfrageRef.current?.abort();
            letzteRef.current = null;
            eintraegeRef.current = [];
            entferneMarkierungen(quelle);
            setAnzahl(0);
            setPopup(null);
        };
    }, [aktiv, pruefen, quelle, rootRef, sofortPruefen]);

    // Beim Schließen des Fensters (Unmount) die Highlights sicher entfernen.
    useEffect(() => () => entferneMarkierungen(quelle), [quelle]);

    // ── Popup ────────────────────────────────────────────────────────────
    const oeffnen = useCallback((eintrag: Eintrag) => {
        const r = eintrag.bereich.getBoundingClientRect();
        const left = Math.max(8, Math.min(r.left, aktivesFenster().innerWidth - POPUP_BREITE - 8));
        setPopup({ eintrag, top: r.bottom + 4, left, aktiv: -1 });
    }, []);

    const schliessen = useCallback(() => setPopup(null), []);

    /** Noch derselbe Wortlaut an der Stelle? Sonst nicht anfassen. */
    const gueltig = (e: Eintrag) => !e.bereich.collapsed && e.bereich.toString() === e.wort;

    const uebernehmen = useCallback(
        (eintrag: Eintrag, vorschlag: string) => {
            const root = rootRef.current;
            setPopup(null);
            if (!root || !gueltig(eintrag)) return;
            root.focus();
            const sel = aktiveAuswahl();
            sel?.removeAllRanges();
            sel?.addRange(eintrag.bereich.cloneRange());
            const ok =
                typeof document.execCommand === "function" &&
                aktivesDokument().execCommand("insertText", false, vorschlag) === true;
            if (!ok) {
                // Ersatzweg ohne execCommand: ersetzt den Text direkt und meldet
                // die Änderung wie eine Eingabe (Entwurf gilt als geändert).
                const r = eintrag.bereich.cloneRange();
                r.deleteContents();
                const t = document.createTextNode(vorschlag);
                r.insertNode(t);
                const s = aktivesDokument().createRange();
                s.setStart(t, t.data.length);
                s.collapse(true);
                sel?.removeAllRanges();
                sel?.addRange(s);
                root.dispatchEvent(new Event("input", { bubbles: true }));
            }
            zeigen(eintraegeRef.current.filter((e) => e !== eintrag));
        },
        [rootRef, zeigen],
    );

    const ignorieren = useCallback(
        (eintrag: Eintrag) => {
            setPopup(null);
            const key = ignorierSchluessel(eintrag.treffer.regel, eintrag.wort);
            ignoriertRef.current.add(key);
            zeigen(eintraegeRef.current.filter((e) => ignorierSchluessel(e.treffer.regel, e.wort) !== key));
        },
        [zeigen],
    );

    // Klick oder Rechtsklick auf einen markierten Bereich öffnet die Vorschläge.
    useEffect(() => {
        const root = rootRef.current;
        if (!aktiv || !root) return;
        const eintragAn = (node: Node, off: number) =>
            eintraegeRef.current.find((e) => {
                try {
                    return gueltig(e) && e.bereich.isPointInRange(node, off);
                } catch {
                    return false;
                }
            });
        const onClick = () => {
            const sel = aktiveAuswahl();
            if (!sel || !sel.isCollapsed || !sel.focusNode) return;
            const treffer = eintragAn(sel.focusNode, sel.focusOffset);
            if (treffer) oeffnen(treffer);
            else setPopup(null);
        };
        /**
         * Rechtsklick: Mit eigener Prüfung ist die des Browsers aus — sein Menü
         * hätte keine Vorschläge mehr, und die Nutzer kennen den Rechtsklick
         * als Weg dorthin. Deshalb öffnet ein Rechtsklick AUF EINE MARKIERUNG
         * die Vorschläge, und zwar vor dem Listen-Menü (T-347): Die Markierung
         * ist die genauere Stelle, das Listen-Menü bleibt mit einem Rechtsklick
         * neben das Wort erreichbar. Der Listener sitzt nativ am Editor und
         * läuft damit vor Reacts `onContextMenu`; `stopPropagation` hält das
         * Listen-Menü fern. Umschalt+Rechtsklick bleibt das Browser-Menü.
         */
        const onContextMenu = (e: MouseEvent) => {
            if (e.shiftKey) return;
            const punkt = punktAn(e.clientX, e.clientY);
            const treffer = punkt && eintragAn(punkt.node, punkt.offset);
            if (!treffer) return;
            e.preventDefault();
            e.stopPropagation();
            oeffnen(treffer);
        };
        root.addEventListener("click", onClick);
        root.addEventListener("contextmenu", onContextMenu);
        return () => {
            root.removeEventListener("click", onClick);
            root.removeEventListener("contextmenu", onContextMenu);
        };
    }, [aktiv, oeffnen, rootRef]);

    // Tastatur, solange das Popup offen ist: Esc schließt NUR das Popup (nicht
    // das Verfassen-Fenster), Pfeile wählen einen Vorschlag, Enter übernimmt.
    useEffect(() => {
        const root = rootRef.current;
        if (!popup || !root) return;
        const n = popup.eintrag.treffer.vorschlaege.length;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                e.preventDefault();
                e.stopPropagation();
                setPopup(null);
            } else if ((e.key === "ArrowDown" || e.key === "ArrowUp") && n > 0) {
                e.preventDefault();
                e.stopPropagation();
                const vor = e.key === "ArrowDown";
                setPopup((p) => {
                    if (!p) return p;
                    const neu = p.aktiv < 0 ? (vor ? 0 : n - 1) : (p.aktiv + (vor ? 1 : -1) + n) % n;
                    return { ...p, aktiv: neu };
                });
            } else if (e.key === "Enter" && popup.aktiv >= 0) {
                e.preventDefault();
                e.stopPropagation();
                uebernehmen(popup.eintrag, popup.eintrag.treffer.vorschlaege[popup.aktiv]);
            } else if (!["Shift", "Control", "Alt", "Meta"].includes(e.key)) {
                setPopup(null);
            }
        };
        const onDown = (e: MouseEvent) => {
            if (popupElRef.current?.contains(e.target as Node)) return;
            setPopup(null);
        };
        const onScroll = (e: Event) => {
            if (popupElRef.current?.contains(e.target as Node)) return;
            setPopup(null);
        };
        root.addEventListener("keydown", onKey);
        alleDokumente.addEventListener("mousedown", onDown, true);
        alleFenster.addEventListener("scroll", onScroll, true);
        return () => {
            root.removeEventListener("keydown", onKey);
            alleDokumente.removeEventListener("mousedown", onDown, true);
            alleFenster.removeEventListener("scroll", onScroll, true);
        };
    }, [popup, rootRef, uebernehmen]);

    const zumErsten = useCallback((): boolean => {
        const erster = eintraegeRef.current.find(gueltig);
        const root = rootRef.current;
        if (!erster || !root) return false;
        root.focus();
        const sel = aktiveAuswahl();
        const r = erster.bereich.cloneRange();
        r.collapse(true);
        sel?.removeAllRanges();
        sel?.addRange(r);
        const el = erster.bereich.startContainer.parentElement;
        el?.scrollIntoView?.({ block: "nearest" });
        oeffnen(erster);
        return true;
    }, [oeffnen, rootRef]);

    const popupKnoten =
        popup &&
        createPortal(
            <div
                ref={popupElRef}
                className="rte-rs-pop"
                role="dialog"
                aria-label="Rechtschreibprüfung"
                style={{ top: popup.top, left: popup.left }}
                // Fokus und Cursor bleiben im Editor.
                onMouseDown={(e) => e.preventDefault()}
            >
                <div className="rte-rs-art">
                    <span
                        className={
                            popup.eintrag.treffer.art === "rechtschreibung"
                                ? "rte-rs-punkt"
                                : "rte-rs-punkt rte-rs-punkt-grammatik"
                        }
                    />
                    {popup.eintrag.treffer.art === "rechtschreibung" ? "Rechtschreibung" : "Grammatik & Zeichensetzung"}
                </div>
                <p className="rte-rs-nachricht">{popup.eintrag.treffer.nachricht}</p>
                {popup.eintrag.treffer.vorschlaege.length ? (
                    <div className="rte-rs-vorschlaege">
                        {popup.eintrag.treffer.vorschlaege.map((v, i) => (
                            <button
                                key={`${i}-${v}`}
                                type="button"
                                className="rte-rs-vorschlag"
                                data-aktiv={i === popup.aktiv ? "true" : undefined}
                                title={`„${popup.eintrag.wort}“ durch „${v}“ ersetzen`}
                                onClick={() => uebernehmen(popup.eintrag, v)}
                            >
                                {v.trim() === "" ? "␣" : v}
                            </button>
                        ))}
                    </div>
                ) : (
                    <p className="rte-rs-leer">Keine Vorschläge.</p>
                )}
                <div className="rte-rs-knoepfe">
                    <button type="button" className="rte-rs-knopf" onClick={() => ignorieren(popup.eintrag)}>
                        Ignorieren
                    </button>
                    <button type="button" className="rte-rs-knopf" onClick={schliessen}>
                        Schließen
                    </button>
                </div>
            </div>,
            document.body,
        );

    return { aktiv, anzahl, zumErsten, popup: popupKnoten || null };
}
